import styled from '@emotion/styled';
import { ThemeColorsType } from 'src/domains/common/themeColors';

interface PreviewThemePropsType {
    theme: ThemeColorsType;
}

/** Blocking popups on website use Montserrat / Open Sans, not backoffice Inter. */
const previewFontFamily = "'Montserrat', 'Open Sans', Arial, sans-serif";

export const PreviewOverlay = styled('div', { label: 'PreviewOverlay' })<PreviewThemePropsType>`
    align-items: flex-start;
    background: ${({ theme }): string => theme.compliancePopup.bgColor};
    color: ${({ theme }): string => theme.compliancePopup.txtColor};
    display: flex;
    font-family: ${previewFontFamily};
    inset: 0;
    justify-content: center;
    overflow-y: auto;
    padding: 24px;
    position: fixed;
    scrollbar-color: rgba(255, 255, 255, 0.35) transparent;
    scrollbar-width: thin;
    z-index: 100;

    ::-webkit-scrollbar {
        width: 8px;
    }

    ::-webkit-scrollbar-track {
        background: transparent;
    }

    @media (min-width: 768px) {
        padding: 16px;
    }
`;

export const PreviewCloseButton = styled('button', { label: 'PreviewCloseButton' })<PreviewThemePropsType>`
    appearance: none;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    color: ${({ theme }): string => theme.compliancePopup.txtColor};
    cursor: pointer;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
    padding: 8px 12px;

    &:hover {
        background: rgba(255, 255, 255, 0.18);
    }
`;

export const PreviewContent = styled('div', { label: 'PreviewContent' })`
    box-sizing: border-box;
    margin: auto;
    max-width: 500px;
    position: relative;
    text-align: center;
    width: 100%;
`;

/** Matches website PopupHeaderSaferGamblingClamped exactly. */
export const PreviewHeader = styled('h2', { label: 'PreviewHeader' })`
    color: inherit;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.4;
    /* stylelint-disable-next-line value-no-vendor-prefix */
    display: -webkit-box;
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-line-clamp: 5;
    /* stylelint-disable-next-line property-no-vendor-prefix */
    -webkit-box-orient: vertical;
    margin: 48px 0 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    word-break: break-word;

    @media (min-width: 768px) {
        line-height: 1.3334;
    }
`;

/** Matches website PopupDescription + CmsMarkersOfHarmNotification inline styles. */
export const PreviewDescription = styled('div', { label: 'PreviewDescription' })<PreviewThemePropsType>`
    color: ${({ theme }): string => theme.compliancePopup.bodyTxtColor};
    font-size: 12px;
    line-height: 1.3334;
    margin: 16px 0;
    max-height: 240px;
    overflow-x: hidden;
    overflow-y: auto;
    overflow-wrap: anywhere;
    overscroll-behavior: contain;
    padding-right: 6px;
    scrollbar-color: rgba(255, 255, 255, 0.35) transparent;
    scrollbar-width: thin;
    word-break: break-word;

    &:first-of-type {
        margin-top: 8px;
    }

    ::-webkit-scrollbar {
        width: 6px;
    }

    ::-webkit-scrollbar-track {
        background: transparent;
    }

    ::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.35);
        border-radius: 999px;
    }

    @media (min-width: 768px) {
        font-size: 14px;
        line-height: 1.4286;
    }

    p,
    span,
    div,
    li,
    font {
        color: inherit;
        font-size: inherit !important;
        line-height: inherit;
        margin: 0;
    }

    a {
        color: inherit;
        font-weight: 700;
        text-decoration: underline;
    }

    ul,
    ol {
        margin: 0;
        padding-left: 1.25rem;
        text-align: left;
    }
`;

export const PreviewActions = styled('div', { label: 'PreviewActions' })`
    margin-top: 0;
    width: 100%;
`;

const previewButtonStyles = `
    appearance: none;
    border: none;
    border-radius: 4px;
    cursor: default;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.43;
    min-height: 44px;
    overflow: hidden;
    padding: 10px 16px;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
`;

export const PreviewPrimaryButton = styled('button', { label: 'PreviewPrimaryButton' })<PreviewThemePropsType>`
    ${previewButtonStyles}
    background: ${({ theme }): string => theme.compliancePopup.buttonBgColor};
    color: ${({ theme }): string => theme.compliancePopup.buttonTxtColor};
    margin-bottom: 4px;

    @media (min-width: 768px) {
        margin-top: 8px;
    }
`;

export const PreviewSecondaryButton = styled('button', { label: 'PreviewSecondaryButton' })<PreviewThemePropsType>`
    ${previewButtonStyles}
    background: ${({ theme }): string => theme.compliancePopup.secondaryBtnBgColor};
    color: ${({ theme }): string => theme.compliancePopup.txtColor};
    margin-top: 8px;
    width: 100%;
`;

export const PreviewTopBar = styled('div', { label: 'PreviewTopBar' })`
    display: flex;
    gap: 8px;
    position: fixed;
    right: 16px;
    top: 16px;
    z-index: 101;
`;

export const PreviewDeviceToggle = styled('div', { label: 'PreviewDeviceToggle' })`
    display: flex;
    gap: 4px;
`;

export const PreviewDeviceButton = styled('button', { label: 'PreviewDeviceButton' })<{ isActive: boolean }>`
    align-items: center;
    appearance: none;
    background: ${({ isActive }): string => (isActive ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.08)')};
    border: 1px solid ${({ isActive }): string => (isActive ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.15)')};
    border-radius: 4px;
    color: inherit;
    cursor: pointer;
    display: inline-flex;
    justify-content: center;
    padding: 6px 10px;

    &:hover {
        background: rgba(255, 255, 255, 0.18);
    }
`;

/**
 * iPhone 15 frame (393 × 852 CSS px).
 * Forces mobile styles from the website: padding 16px, smaller fonts,
 * matching CmsMarkersOfHarmNotification + BlockingNotifications.style.
 */
export const PreviewMobileOuter = styled('div', { label: 'PreviewMobileOuter' })`
    align-self: center;
    border-radius: 48px;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.3), 0 8px 40px rgba(0, 0, 0, 0.4);
    flex-shrink: 0;
    height: min(852px, calc(100vh - 48px));
    margin: auto;
    overflow: hidden;
    width: 393px;
`;

export const PreviewMobileFrame = styled('div', { label: 'PreviewMobileFrame' })<PreviewThemePropsType>`
    background: ${({ theme }): string => theme.compliancePopup.bgColor};
    border: 3px solid rgba(255, 255, 255, 0.2);
    border-radius: 48px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 100%;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 24px;
    position: relative;
    scrollbar-color: rgba(255, 255, 255, 0.35) transparent;
    scrollbar-gutter: stable;
    scrollbar-width: thin;
    width: 100%;
`;

export const PreviewMobileContent = styled('div', { label: 'PreviewMobileContent' })`
    box-sizing: border-box;
    margin: auto;
    max-width: 500px;
    position: relative;
    text-align: center;
    width: 100%;
`;

export const PreviewMobileHeader = styled(PreviewHeader, { label: 'PreviewMobileHeader' })`
    line-height: 1.4;
`;

export const PreviewMobileDescription = styled(PreviewDescription, { label: 'PreviewMobileDescription' })`
    font-size: 12px;
    line-height: 1.3334;
    margin: 8px 0 16px;
`;

export const PreviewMobilePrimaryButton = styled(PreviewPrimaryButton, { label: 'PreviewMobilePrimaryButton' })`
    margin-top: 0;
`;

export const PreviewMobileNotch = styled('div', { label: 'PreviewMobileNotch' })`
    background: rgba(0, 0, 0, 0.85);
    border-radius: 0 0 20px 20px;
    height: 34px;
    left: 50%;
    pointer-events: none;
    position: absolute;
    top: 0;
    transform: translateX(-50%);
    width: 126px;
    z-index: 1;
`;
